import { useState } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { motion, AnimatePresence } from 'framer-motion';
import TaskDetailsModal from './TaskDetailsModal';
import { taskService } from '../../../services/task.service';
import Column from './Column';
import SortableTask from './SortableTask';
import './KanbanBoard.css';

const KanbanBoard = ({ tasks, onDragEnd: onDragEndProp }) => {
  const [selectedTask, setSelectedTask] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [activeTask, setActiveTask] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setIsDetailsModalOpen(true);
  };

  const handleUpdateTask = async (taskId, updates) => {
    try {
      await taskService.updateTask(taskId, updates);
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleDragStart = (event) => {
    const { active } = event;
    const task = tasks[active.data.current?.sortable.containerId]?.find(t => t.id === active.id);
    if (task) {
      setActiveTask(task);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveTask(null);

    if (!over) return;

    const activeContainer = active.data.current?.sortable.containerId;
    const overId = over.id;
    
    if (typeof overId === 'string' && overId.endsWith('-trash')) {
      const result = {
        source: {
          droppableId: activeContainer,
          index: active.data.current?.sortable.index,
        },
        destination: null
      };
      onDragEndProp(result);
      return;
    }

    const overContainer = over.data.current?.sortable.containerId || over.id;

    if (activeContainer !== overContainer) {
      const result = {
        source: {
          droppableId: activeContainer,
          index: active.data.current?.sortable.index,
        },
        destination: {
          droppableId: overContainer,
          index: over.data.current?.sortable.index || 0,
        },
      };
      onDragEndProp(result);
    }
  };

  const columns = [
    { id: 'backlog', title: 'Backlog' },
    { id: 'inProgress', title: 'In Progress' },
    { id: 'inReview', title: 'In Review' },
    { id: 'completed', title: 'Completed' }
  ];

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="kanban-board">
          {columns.map(column => (
            <Column
              key={column.id}
              id={column.id}
              title={column.title}
              tasks={tasks[column.id] || []}
              onTaskClick={handleTaskClick}
            />
          ))}
        </div>

        <DragOverlay>
          {activeTask ? (
            <SortableTask
              id={activeTask.id}
              task={activeTask}
              isDragging
              isOverlay
            />
          ) : null}
        </DragOverlay>
      </DndContext>

      <TaskDetailsModal
        task={selectedTask}
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        onUpdate={handleUpdateTask}
      />
    </>
  );
};

export default KanbanBoard; 